// verifyLockState — the pure decision behind a field's "locked verified" state.
//
// A field with `verifyAction` normally renders a Verify button that flips to a
// soft success label once its `verifyResultField` sibling is true, and back to
// a button the moment the verified value is edited. That is right for a check
// that only ever proved a mailbox exists.
//
// It is NOT right once something irreversible has happened off the back of that
// check — e.g. a login was provisioned for the address, stamped onto the SAME
// row by a workflow. From then on the row is a record of a real account, not a
// re-runnable check, so the control must stop offering "Verify" at all.
//
// `verifyLockedWhenFields` names the sibling row keys that carry that evidence
// (comma / space / newline separated, resolved row-scoped exactly like
// `verifyResultField`). Nothing here knows a module, group or field name — the
// employers/recruiters/isAccountCreate case is pure admin config.
//
// This lives in its own module purely so it can be unit-tested: any vitest file
// that imports EditFormV1 drags in react-pdf, which dies on jsdom's missing
// DOMMatrix. The logic is the part worth testing, so the logic is the part that
// is importable on its own.

// isTruthyFlag decides whether ONE stored row value counts as "set".
//
// It has to be looser than `=== true`: a lock field is usually a boolean flag
// (isAccountCreate) but an admin can just as reasonably point at the id or
// timestamp the same workflow stamped (accountUserId: 144,
// accountCreatedAt: "2026-08-05T…"), and those are the strongest evidence of
// all. It also has to be tighter than JS truthiness, because a hidden <Input/>
// carrier stringifies whatever it holds — so the STRING "false" must stay
// false, and so must "0" and an empty string.
const FALSY_TEXT = new Set(['', 'false', '0', 'no', 'null', 'undefined']);

export function isTruthyFlag(value) {
  if (value === undefined || value === null || value === false) return false;
  if (typeof value === 'number') return value !== 0 && !Number.isNaN(value);
  if (typeof value === 'string') return !FALSY_TEXT.has(value.trim().toLowerCase());
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

// parseLockFieldKeys turns the admin's free-text list into field keys. Commas,
// spaces and newlines all separate, so "isAccountCreate, accountUserId" and
// "isAccountCreate accountUserId" mean the same thing. Returns [] for anything
// unset — which is what keeps every field configured before this existed on
// exactly its old behaviour.
export function parseLockFieldKeys(config) {
  if (Array.isArray(config)) {
    return config.map((k) => String(k ?? '').trim()).filter(Boolean);
  }
  return String(config ?? '')
    .split(/[\s,]+/)
    .map((k) => k.trim())
    .filter(Boolean);
}

// isVerifyLocked — true when the control must render as a non-interactive
// "Verified" state instead of a button.
//
// `lockKeys` are ADDITIONAL to the existing result field: every lock key must be
// truthy AND, when the field configures a `verifyResultField`, that result must
// be true too. So "account created" alone never locks a row whose mailbox check
// failed — which is the whole point of showing the state at all.
//
// readSibling(key) resolves one sibling key in the same scope the caller uses
// for verifyResultField (the addRow row when nested, the form root otherwise).
export function isVerifyLocked({ lockKeys = [], resultValue, hasResultField = true, readSibling }) {
  if (!lockKeys.length || typeof readSibling !== 'function') return false;
  if (hasResultField && !isTruthyFlag(resultValue)) return false;
  return lockKeys.every((key) => isTruthyFlag(readSibling(key)));
}

// readAtPath — plain deep read used to resolve a sibling path against a whole
// form-values snapshot (Form.useWatch's selector form). Kept here so the lock
// decision has no antd dependency and stays testable.
export function readAtPath(values, path = []) {
  let cursor = values;
  for (const segment of path) {
    if (cursor === undefined || cursor === null) return undefined;
    cursor = cursor[segment];
  }
  return cursor;
}
